import { OpayoCardDetails, OpayoSessionKeyResponse } from '../types';

declare global {
  interface Window {
    opayoOwnForm?: (options: { merchantSessionKey: string }) => {
      tokeniseCardDetails: (options: {
        cardDetails: {
          cardholderName: string;
          cardNumber: string;
          expiryDate: string;
          securityCode: string;
        };
        onTokenised: (result: any) => void;
      }) => void;
    };
    OpayoConfig?: {
      restHost?: string;
      restBaseURL?: string;
    };
  }
}

const OPAYO_SCRIPT_SRC = 'https://assets.ci.opayo.cloud/assets/js/opayo-1.2.40.js';
const OPAYO_SCRIPT_INTEGRITY =
  'sha512-ZplJXUTeRh13LTLjfwydrUFpRJaHOoSIKdoMQP4s1gWyJoXqsTqxBeGdt4fQyfNq/Xo21u8IGaA3PjLfiefZJw==';

let scriptPromise: Promise<void> | null = null;

const loadOpayoScript = async () => {
  if (typeof window === 'undefined') return;
  if (window.opayoOwnForm) return;
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src=\"${OPAYO_SCRIPT_SRC}\"]`
    );
    if (existing) {
      if (window.opayoOwnForm) {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Failed to load Opayo script.')), {
        once: true
      });
      window.setTimeout(() => {
        if (window.opayoOwnForm) {
          resolve();
          return;
        }
        reject(new Error('Opayo script did not initialise.'));
      }, 4000);
      return;
    }

    const script = document.createElement('script');
    script.src = OPAYO_SCRIPT_SRC;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.integrity = OPAYO_SCRIPT_INTEGRITY;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Opayo script.'));
    document.head.appendChild(script);
  });

  return scriptPromise;
};

const getTokenizeError = (result: any) => {
  const firstError =
    result?.errors?.[0]?.description ||
    result?.errors?.[0]?.message ||
    result?.error?.description ||
    result?.error?.message;
  return firstError || 'Failed to tokenize card details.';
};

export const tokenizeCardDetails = async ({
  session,
  cardDetails
}: {
  session: OpayoSessionKeyResponse;
  cardDetails: OpayoCardDetails;
}) => {
  await loadOpayoScript();

  if (!window.opayoOwnForm) {
    throw new Error('Opayo script is not available.');
  }

  if (window.OpayoConfig) {
    if (session.restHost) {
      window.OpayoConfig.restHost = session.restHost;
    }
    if (session.restBaseURL) {
      window.OpayoConfig.restBaseURL = session.restBaseURL;
    }
  }

  return new Promise<string>((resolve, reject) => {
    try {
      window.opayoOwnForm!({ merchantSessionKey: session.merchantSessionKey }).tokeniseCardDetails({
        cardDetails: {
          cardholderName: cardDetails.cardholderName,
          cardNumber: cardDetails.number,
          expiryDate: cardDetails.expiry,
          securityCode: cardDetails.cvc
        },
        onTokenised: (result: any) => {
          if (result?.success && result?.cardIdentifier) {
            resolve(result.cardIdentifier);
            return;
          }
          reject(new Error(getTokenizeError(result)));
        }
      });
    } catch (err: any) {
      reject(new Error(err?.message || 'Failed to tokenize card details.'));
    }
  });
};
